// ============================================================================
// RAID DISPLAY CONFIG
//
// The single place per-type wording lives. The backend stores one canonical
// status per state; a question that is "resolved" reads as Answered, an
// assumption as Validated, a risk as Mitigated. Every surface — badge, inline
// list, register, export — must call statusLabel rather than relabelling
// locally, or the wording drifts between screens.
// ============================================================================

import {
  AlertTriangle, HelpCircle, Link2, Lightbulb, Flame, LucideIcon,
} from 'lucide-react';
import {
  RaidType, RaidStatus, RaidDependencyType, RaidBlockingSeverity, RaidOwnerOrg,
} from '../../../types/raid';

interface RaidTypeConfig {
  label: string;
  plural: string;
  icon: LucideIcon;
  /** Chip colours for the type itself. */
  chip: string;
  /** How 'resolved' reads for this type. */
  resolvedLabel: string;
  /** How 'invalidated' reads for this type. */
  invalidatedLabel: string;
  hint: string;
}

export const RAID_TYPES: Record<RaidType, RaidTypeConfig> = {
  risk: {
    label: 'Risk',
    plural: 'Risks',
    icon: Flame,
    chip: 'bg-red-50 text-red-600 border-red-200',
    resolvedLabel: 'Mitigated',
    invalidatedLabel: 'Materialised',
    hint: 'Might happen, and would hurt if it did.',
  },
  assumption: {
    label: 'Assumption',
    plural: 'Assumptions',
    icon: Lightbulb,
    chip: 'bg-violet-50 text-violet-600 border-violet-200',
    resolvedLabel: 'Validated',
    invalidatedLabel: 'Proven false',
    hint: 'Believed true but unverified. Work built on it is at risk if it is wrong.',
  },
  issue: {
    label: 'Issue',
    plural: 'Issues',
    icon: AlertTriangle,
    chip: 'bg-orange-50 text-orange-600 border-orange-200',
    resolvedLabel: 'Fixed',
    invalidatedLabel: 'Not an issue',
    hint: 'A problem that has already happened and needs fixing.',
  },
  dependency: {
    label: 'Dependency',
    plural: 'Dependencies',
    icon: Link2,
    chip: 'bg-amber-50 text-amber-700 border-amber-200',
    resolvedLabel: 'Delivered',
    invalidatedLabel: 'Fell through',
    hint: 'Needs someone else to act before this can move.',
  },
  question: {
    label: 'Question',
    plural: 'Questions',
    icon: HelpCircle,
    chip: 'bg-sky-50 text-sky-600 border-sky-200',
    resolvedLabel: 'Answered',
    invalidatedLabel: 'Withdrawn',
    hint: 'Missing information that needs an answer.',
  },
};

export const RAID_TYPE_ORDER: RaidType[] = ['dependency', 'question', 'risk', 'issue', 'assumption'];

export const RAID_STATUSES: RaidStatus[] = ['open', 'resolved', 'deferred', 'wont_resolve', 'invalidated'];

/** The one place a stored status becomes words a human reads. */
export function statusLabel(type: RaidType, status: RaidStatus): string {
  switch (status) {
    case 'open': return 'Open';
    case 'resolved': return RAID_TYPES[type].resolvedLabel;
    case 'invalidated': return RAID_TYPES[type].invalidatedLabel;
    case 'deferred': return 'Deferred';
    default: return "Won't action";
  }
}

export function statusChip(status: RaidStatus): string {
  switch (status) {
    case 'open': return 'bg-slate-100 text-slate-600 border-slate-200';
    case 'resolved': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'deferred': return 'bg-blue-50 text-blue-600 border-blue-200';
    case 'invalidated': return 'bg-rose-50 text-rose-600 border-rose-200';
    default: return 'bg-slate-50 text-slate-400 border-slate-200';
  }
}

// ============================================================================
// Risk scoring
// ============================================================================

/** Green 1-6, Amber 8-12, Red 15-25 on a 5x5 probability x impact matrix. */
export function ragFor(probability?: number, impact?: number): 'green' | 'amber' | 'red' | undefined {
  if (typeof probability !== 'number' || typeof impact !== 'number') return undefined;
  return ragForScore(probability * impact);
}

export function ragForScore(score?: number | null): 'green' | 'amber' | 'red' | undefined {
  if (typeof score !== 'number') return undefined;
  if (score >= 15) return 'red';
  if (score >= 8) return 'amber';
  return 'green';
}

export function ragChip(rag?: 'green' | 'amber' | 'red' | null): string {
  switch (rag) {
    case 'red': return 'bg-red-100 text-red-700 border-red-300';
    case 'amber': return 'bg-amber-100 text-amber-700 border-amber-300';
    case 'green': return 'bg-emerald-100 text-emerald-700 border-emerald-300';
    default: return 'bg-slate-100 text-slate-500 border-slate-200';
  }
}

// ============================================================================
// Enum labels
// ============================================================================

export const DEPENDENCY_TYPES: { value: RaidDependencyType; label: string }[] = [
  { value: 'external_party', label: 'External party' },
  { value: 'contractual', label: 'Contractual' },
  { value: 'legal', label: 'Legal' },
  { value: 'technical', label: 'Technical' },
  { value: 'data', label: 'Data' },
  { value: 'decision', label: 'Decision' },
];

export const BLOCKING_SEVERITIES: { value: RaidBlockingSeverity; label: string; hint: string }[] = [
  { value: 'blocks', label: 'Blocks', hint: 'Work cannot proceed until this clears' },
  { value: 'constrains', label: 'Constrains', hint: 'Work can proceed but is limited' },
  { value: 'informs', label: 'Informs', hint: 'Useful context, not a constraint' },
];

export const OWNER_ORGS: { value: RaidOwnerOrg; label: string }[] = [
  { value: 'client', label: 'Client' },
  { value: 'version1', label: 'Version 1' },
];

export const LINK_TARGET_LABELS: Record<string, string> = {
  user_story: 'User story',
  nfr: 'NFR',
  feature: 'Feature',
  adr: 'ADR',
  challenge_area: 'Challenge area',
  raid_item: 'RAID item',
};

/** Everything except raid_item is only unique within a session. */
export const LINK_TYPES_NEEDING_SESSION = ['user_story', 'nfr', 'feature', 'adr', 'challenge_area'];
